/**
 * The exposure section pages' questions, fields and layout, ported from H2's
 * `04-05-GuidedExposures.dc.html` `SCHEMA` (owner decision 2026-11-03, m1-portal-h2-fidelity: the
 * sections ask exactly H2's questions, and where H2 and the build differ, H2 wins).
 *
 * H2's sample data is not ported. Its `hint` lists, the options of every "Which X?" select that
 * names a customer record, per-property limits and every `current` figure are sample records; in
 * the portal they come from the renewal's Epic data through `on-file-fields.ts`. Fixed
 * vocabularies (change types, building systems, unit types, Included/Excluded,
 * Yes/No) stay here as literals.
 *
 * Mapping from H2's field properties:
 * - the change-type radio (`status`/`changeType`) is the row's `outcome`;
 * - `showIf`/`showIfAll` is `showIf` (every condition must hold; `field: "outcome"` reads the
 *   change type, `olderThan` reads a four-digit year);
 * - `hideUnlessExistingPicked` is `hideUntilPicked` (the question's on-file picker);
 * - `t: 'select'` naming customer records is `type: "picker"`;
 * - `year`, `futureOk`, `maxToday`, `optional`, `defaultVal`, `explain`/`explainLabel` keep their
 *   meaning; `current` is `onFileHelp`, a help trigger whose text is the figure on file;
 * - `detailFields`, `detailRowFilter`, `detailHelp` and the `detailHeader*` properties are
 *   `additionalDetailsPage` and `additionalDetailsTriggerOutcomes`.
 *
 * `fieldId` values that double as a SECTION_TABLE row's `additionalDetailsRules[].fieldId`
 * (routing/route-table.ts) are named identically to that rule, so `state/section-instances.ts`
 * reads these repeat answers unchanged. Every other field id is this schema's own: answers are
 * opaque to the routing layer and to the API.
 */
import type { SectionId } from "../../routing/route-table";

export type ExposureQuestionType = "yesno-detail" | "yesno-repeat" | "checkbox-list" | "currency" | "limits";

/** One H2 show rule. `field: "outcome"` reads the row's change type. */
export type FieldCondition =
  | { readonly field: string; readonly is: string }
  | { readonly field: string; readonly in: readonly string[] }
  | { readonly field: string; readonly olderThan: number };

export interface FieldOption {
  readonly value: string;
  readonly label: string;
}

export interface RepeatFieldSpec {
  readonly id: string;
  readonly label: string;
  /**
   * `picker`: a "Which X?" select over the renewal's on-file records (`on-file-fields.ts`'s
   * `picker` for the question, matched on this field's `id`). `yesno` and `radio` are radio
   * groups; `checkYear` is H2's one checkbox per option, each revealing its own year field;
   * `static` shows a prefilled value as text; `year` is a four-digit year.
   */
  readonly type: "text" | "select" | "currency" | "year" | "picker" | "yesno" | "radio" | "date" | "checkYear" | "static";
  readonly options?: readonly FieldOption[];
  readonly wide?: boolean;
  /** H2 `showIf`/`showIfAll`: the field shows only while every condition holds, and only while
   * each field a condition reads is itself shown. A hidden field is neither validated nor kept. */
  readonly showIf?: readonly FieldCondition[];
  /** H2 `hideUnlessExistingPicked`: shown only once the question's picker holds an on-file record. */
  readonly hideUntilPicked?: boolean;
  /** H2 `futureOk`: a year after this one is accepted. */
  readonly futureOk?: boolean;
  /** H2 `maxToday`: a date after today is refused. */
  readonly maxToday?: boolean;
  readonly optional?: boolean;
  /** H2 `defaultVal`: shown and saved while the field is untouched, so it is never blank. */
  readonly defaultValue?: string;
  /** H2 `explain`/`explainLabel`: fixed guidance behind a help trigger after the label. */
  readonly explain?: { readonly triggerLabel: string; readonly text: string };
  /** H2 `current`: a help trigger after the label whose text is the figure the renewal holds on
   * file, read from `screenKey`'s `attribute`; the default message when there is none. */
  readonly onFileHelp?: {
    readonly itemsLabel: string;
    readonly screenKey?: string;
    readonly attribute?: string;
  };
  /** H2 prefilled `current`: once an on-file record is picked, a help trigger shows the value
   * that record holds for this field; `itemsLabel` names it in the default message. */
  readonly pickedHelp?: { readonly itemsLabel: string };
  /** H2 `soldAware` on a select: records marked sold in the question's sold source drop out. */
  readonly soldAware?: boolean;
  /** H2 `emptyLabel`: the select's placeholder, and the error message's wording. */
  readonly emptyLabel?: string;
  /** H2 `noneLeftLabel`: the placeholder once nothing is left to select. */
  readonly noneLeftLabel?: string;
  /** A fixed choice offered after the on-file records (H2's "Add a new commodity"). */
  readonly newOption?: FieldOption;
}

/** The value a picker field holds when its `newOption` is chosen. */
export const PICKER_NEW_OPTION_VALUE = "__new__";

function conditionHolds(condition: FieldCondition, value: string): boolean {
  if ("olderThan" in condition) {
    // H2: age-gated fields wait for a plausible four-digit year, then appear once it is old enough.
    const raw = value.trim();
    const year = Number.parseInt(raw, 10);
    const now = new Date().getFullYear();
    return /^\d{4}$/.test(raw) && year <= now && now - year > condition.olderThan;
  }
  if ("in" in condition) {
    return condition.in.includes(value);
  }
  return value === condition.is;
}

/** The values a row's show rules read: its change type as `outcome`, then its own fields. */
export interface ShowRuleRow {
  readonly outcome: string;
  readonly fields: Readonly<Record<string, string>>;
}

/** Whether a field is shown, given the row's answers (H2 `fieldVisible`). `siblings` are the
 * other fields of the same list, so a field whose controlling field is hidden is hidden too. */
export function isRepeatFieldShown(
  field: RepeatFieldSpec,
  item: ShowRuleRow,
  siblings: readonly RepeatFieldSpec[] = [],
  seen: readonly string[] = [],
): boolean {
  for (const condition of field.showIf ?? []) {
    const value = condition.field === "outcome" ? item.outcome : (item.fields[condition.field] ?? "");
    if (!conditionHolds(condition, value)) {
      return false;
    }
    const parent = siblings.find((candidate) => candidate.id === condition.field);
    if (parent !== undefined && !seen.includes(parent.id) && !isRepeatFieldShown(parent, item, siblings, [...seen, field.id])) {
      return false;
    }
  }
  return true;
}

export interface AdditionalDetailsPageSpec {
  readonly heading: string;
  /** H2 `detailHelp`: behind the "Why are we asking for this?" trigger beside the page title. */
  readonly helpText: string;
  readonly fields: readonly RepeatFieldSpec[];
  /** H2 `detailHeaderNoun`: rows are titled "New <noun> <n>", naming the record a replacement replaces. */
  readonly headerNoun?: string;
  /** H2 `detailHeaderField`: the parent row's field naming the item (an address, or the record replaced). */
  readonly headerField?: string;
  /** H2 `detailHeaderFallback`: the title while `headerField` is blank. */
  readonly headerFallback?: string;
}

export interface RepeatBlockSpec {
  /** H2's row title: "<itemLabel> <n>" ("Driver 1"); also names the row in error messages. */
  readonly itemLabel: string;
  /** H2 `noTitle`: rows are titled "Change <n>" instead. */
  readonly noTitle?: boolean;
  /** H2 `single`: exactly one untitled row, with no add or remove. */
  readonly single?: boolean;
  /** The fieldset legend, numbered "<n>a." under the question. */
  readonly legend: string;
  /** H2 `legendHidden`: the legend stays for screen readers only. */
  readonly legendHidden?: boolean;
  readonly addLabel: string;
  /** The change-type radio ("What change has been made?"), first in each row. */
  readonly outcomeOptions?: readonly FieldOption[];
  readonly outcomeLegend?: string;
  readonly fields: readonly RepeatFieldSpec[];
  /** Change types that add an additional-details page after this section (H2
   * `detailRowFilter`). Matches route-table.ts's `additionalDetailsRules` for this field. */
  readonly additionalDetailsTriggerOutcomes?: readonly string[];
  readonly additionalDetailsPage?: AdditionalDetailsPageSpec;
}

export interface ChecklistItemSpec {
  readonly id: string;
  readonly label: string;
  readonly helpText?: string;
}

/** H2 `properties`/`limits`: a property picked from the buildings on file, then each subject of
 * insurance added from what that property holds, with its updated limit. Epic holds no valuation basis, so none is shown (ADR-20261103-hlgd). */
export interface LimitsSpec {
  readonly legend: string;
  /** H2's "Other" subject, offered for every property. */
  readonly otherSubject: FieldOption;
}

export interface ExposureQuestion {
  readonly fieldId: string;
  /** The question's number within its section (H2 numbers them 1, 2, 3...). */
  readonly number: string;
  readonly legend: string;
  readonly type: ExposureQuestionType;
  readonly helpText?: string;
  /** For `type: "yesno-detail"`: the follow-up field's prompt. */
  readonly detailPrompt?: string;
  readonly repeat?: RepeatBlockSpec;
  readonly checklist?: {
    readonly legend: string;
    readonly closingNote?: string;
    readonly items: readonly ChecklistItemSpec[];
    /** The one item that reveals a free-text field when ticked. */
    readonly otherItemId?: string;
  };
  readonly currencyHelp?: string;
  /** H2: fixed help text behind a toggle in the question, named by this label. */
  readonly hintLabel?: string;
  readonly limits?: LimitsSpec;
}

export interface ExposureSectionSchema {
  readonly sectionId: SectionId;
  readonly questions: readonly ExposureQuestion[];
}

const opts = (...labels: readonly string[]): readonly FieldOption[] => labels.map((label) => ({ value: label, label }));

const BUILDING_CHANGE_OPTIONS = opts("Purchased", "Sold", "Renovated", "Expanded");
const ITEM_CHANGE_OPTIONS = opts("Purchased", "Sold", "Replaced");
const PERSON_CHANGE_OPTIONS = opts("Added", "Removed");
const SYSTEM_OPTIONS: readonly FieldOption[] = [
  { value: "roof", label: "Roof" },
  { value: "electrical", label: "Electrical" },
  { value: "hvac", label: "HVAC" },
  { value: "plumbing", label: "Plumbing" },
];
const INCLUSION_OPTIONS: readonly FieldOption[] = [
  { value: "included", label: "Included" },
  { value: "excluded", label: "Excluded" },
];

const WHAT_CHANGED = "What change has been made?";
const BUILDING_MORTGAGE: FieldCondition = { field: "outcome", in: ["Purchased", "Renovated", "Expanded"] };
const ADDED_OR_REPLACED: FieldCondition = { field: "outcome", in: ["Purchased", "Replaced"] };
const SOLD_OR_REPLACED: FieldCondition = { field: "outcome", in: ["Sold", "Replaced"] };
const ADDED: FieldCondition = { field: "outcome", is: "Added" };
const FINANCED: FieldCondition = { field: "financedOrLeased", is: "yes" };

function lienholderExplain(noun: string): RepeatFieldSpec["explain"] {
  return {
    triggerLabel: "More about lienholder name",
    text: `A lienholder is the bank or finance company that holds a legal claim on the ${noun} until the loan or lease is paid off.`,
  };
}

/** H2's details page for a purchased or replaced vehicle, watercraft or piece of equipment. */
function itemDetailsFields(noun: string, kind: "vehicle" | "watercraft" | "equipment"): readonly RepeatFieldSpec[] {
  const explain = lienholderExplain(noun);
  const lienholderAddressExplain = explain === undefined ? undefined : { ...explain, triggerLabel: "More about lienholder address" };
  const identity: readonly RepeatFieldSpec[] =
    kind === "equipment"
      ? [
          { id: "year", label: "Year", type: "year", futureOk: true, showIf: [ADDED_OR_REPLACED] },
          { id: "manufacturer", label: "Manufacturer", type: "text", showIf: [ADDED_OR_REPLACED] },
          { id: "model", label: "Model", type: "text", showIf: [ADDED_OR_REPLACED] },
          { id: "type", label: "Type", type: "text", showIf: [ADDED_OR_REPLACED] },
          { id: "serialNumber", label: "Serial number", type: "text", showIf: [ADDED_OR_REPLACED] },
          { id: "value", label: "Value", type: "currency", showIf: [ADDED_OR_REPLACED] },
        ]
      : [
          { id: "year", label: "Year", type: "year", futureOk: true, showIf: [ADDED_OR_REPLACED] },
          { id: "make", label: "Make", type: "text", showIf: [ADDED_OR_REPLACED] },
          { id: "model", label: "Model", type: "text", showIf: [ADDED_OR_REPLACED] },
          kind === "watercraft"
            ? { id: "serialOrVin", label: "Serial number/VIN", type: "text", showIf: [ADDED_OR_REPLACED] }
            : { id: "vin", label: "VIN", type: "text", showIf: [ADDED_OR_REPLACED] },
          { id: "costNew", label: "Cost new", type: "currency", showIf: [ADDED_OR_REPLACED] },
        ];
  return [
    ...identity,
    { id: "financedOrLeased", label: `Is the ${noun} financed or leased?`, type: "yesno", showIf: [ADDED_OR_REPLACED] },
    {
      id: "lienholderName",
      label: "Lienholder name",
      type: "text",
      wide: true,
      showIf: [FINANCED, ADDED_OR_REPLACED],
      ...(explain !== undefined ? { explain } : {}),
    },
    {
      id: "lienholderAddress",
      label: "Lienholder address",
      type: "text",
      wide: true,
      showIf: [FINANCED, ADDED_OR_REPLACED],
      ...(lienholderAddressExplain !== undefined ? { explain: lienholderAddressExplain } : {}),
    },
  ];
}

/** H2's "Which X?" picker, shown for a sold or replaced item. */
function whichItem(id: string, label: string, emptyLabel: string, noneLeftLabel?: string): RepeatFieldSpec {
  return {
    id,
    label,
    type: "picker",
    wide: true,
    emptyLabel,
    ...(noneLeftLabel !== undefined ? { noneLeftLabel } : {}),
    showIf: [SOLD_OR_REPLACED],
  };
}

function itemChangeQuestion(params: {
  readonly fieldId: string;
  readonly number: string;
  readonly legend: string;
  readonly itemLabel: string;
  readonly picker: RepeatFieldSpec;
  readonly noun: string;
  readonly kind: "vehicle" | "watercraft" | "equipment";
  readonly helpText: string;
  readonly heading: string;
}): ExposureQuestion {
  return {
    fieldId: params.fieldId,
    number: params.number,
    legend: params.legend,
    type: "yesno-repeat",
    repeat: {
      itemLabel: params.itemLabel,
      noTitle: true,
      legend: "Provide details",
      addLabel: "Add another change",
      outcomeLegend: WHAT_CHANGED,
      outcomeOptions: ITEM_CHANGE_OPTIONS,
      fields: [params.picker],
      additionalDetailsTriggerOutcomes: ["Purchased", "Replaced"],
      additionalDetailsPage: {
        heading: params.heading,
        helpText: params.helpText,
        fields: itemDetailsFields(params.noun, params.kind),
        headerNoun: params.noun,
        headerField: params.picker.id,
      },
    },
  };
}

/** H2's driver or operator change: a picker for a removed person, the new person's details. */
function personChangeQuestion(params: {
  readonly fieldId: string;
  readonly number: string;
  readonly legend: string;
  readonly itemLabel: string;
  readonly noTitle: boolean;
  readonly addLabel: string;
  readonly pickerId: string;
  readonly pickerLabel: string;
  readonly emptyLabel: string;
  readonly boating?: boolean;
}): ExposureQuestion {
  return {
    fieldId: params.fieldId,
    number: params.number,
    legend: params.legend,
    type: "yesno-repeat",
    repeat: {
      itemLabel: params.itemLabel,
      ...(params.noTitle ? { noTitle: true } : {}),
      legend: "Provide details",
      addLabel: params.addLabel,
      outcomeLegend: WHAT_CHANGED,
      outcomeOptions: PERSON_CHANGE_OPTIONS,
      fields: [
        {
          id: params.pickerId,
          label: params.pickerLabel,
          type: "picker",
          wide: true,
          emptyLabel: params.emptyLabel,
          showIf: [{ field: "outcome", is: "Removed" }],
        },
        { id: "name", label: "Name", type: "text", wide: true, showIf: [ADDED] },
        { id: "dateOfBirth", label: "Date of birth", type: "date", maxToday: true, showIf: [ADDED] },
        { id: "licenseNumber", label: "Driver's license number", type: "text", showIf: [ADDED] },
        { id: "stateOfIssuance", label: "State of issuance", type: "text", showIf: [ADDED] },
        ...(params.boating === true
          ? [{ id: "yearsOfBoatingExperience", label: "Years of boating experience", type: "text", showIf: [ADDED] } as const]
          : []),
      ],
    },
  };
}

/** H2's class-code estimate question (general liability, marina operators, marine employers). */
function classCodeQuestion(): ExposureQuestion {
  return {
    fieldId: "grossSalesChanged",
    number: "1",
    legend: "Are there any changes to estimated gross sales, payroll, total cost, or receipts?",
    type: "yesno-repeat",
    repeat: {
      itemLabel: "Class code",
      noTitle: true,
      legend: "Update your class code estimates",
      addLabel: "Add another change",
      fields: [
        { id: "classCode", label: "Select class code", type: "picker", wide: true, emptyLabel: "Select a class code" },
        { id: "premiumBasis", label: "Premium basis", type: "static", hideUntilPicked: true },
        { id: "estimate", label: "Estimate", type: "currency", hideUntilPicked: true, pickedHelp: { itemsLabel: "estimates" } },
      ],
    },
  };
}

/** H2's single-row figure question (transportation and commercial trucking gross sales). */
function grossSalesFigureQuestion(screenKey: string, attribute: string): ExposureQuestion {
  return {
    fieldId: "grossSalesChanged",
    number: "1",
    legend: "Are there any changes to estimated gross sales?",
    type: "yesno-repeat",
    repeat: {
      itemLabel: "Sales",
      single: true,
      legend: "Provide updated estimates",
      addLabel: "",
      fields: [
        {
          id: "sales",
          label: "Estimated gross sales",
          type: "currency",
          onFileHelp: { itemsLabel: "estimated gross sales figures", screenKey, attribute },
        },
      ],
    },
  };
}

const EXPOSURE_SCHEMAS: readonly ExposureSectionSchema[] = [
  {
    sectionId: "exposure-business-operations",
    questions: [
      {
        fieldId: "operationsChanged",
        number: "1",
        legend: "Has the nature of your business operations changed during the past 12 months?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
      {
        fieldId: "locationsAddedOrDiscontinued",
        number: "2",
        legend: "Have you added or discontinued any locations?",
        type: "yesno-detail",
        detailPrompt: "Provide details, including the property address",
      },
      {
        fieldId: "newConstruction",
        number: "3",
        legend: "Are any new buildings or structures currently under construction?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Building",
          legend: "Tell us about each building or structure under construction",
          addLabel: "Add another building",
          fields: [
            { id: "propertyAddress", label: "Property address", type: "text", wide: true },
            { id: "costOfConstruction", label: "Cost of construction", type: "currency" },
          ],
        },
      },
      {
        fieldId: "ownershipOrManagementChanged",
        number: "4",
        legend: "Have there been any changes in ownership, management, or organizational structure?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
      {
        fieldId: "mergersAcquisitionsDivestitures",
        number: "5",
        legend: "Have there been any mergers, acquisitions, or divestitures?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
      {
        fieldId: "estimatedAnnualGrossRevenues",
        number: "6",
        legend: "What are the estimated annual gross revenues for the next 12 months?",
        type: "currency",
      },
    ],
  },
  {
    sectionId: "exposure-commercial-property",
    questions: [
      {
        fieldId: "buildingsChanged",
        number: "1",
        legend: "Have any buildings been purchased, sold, renovated, or expanded?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Building",
          noTitle: true,
          legend: "Provide details",
          addLabel: "Add another change",
          outcomeLegend: WHAT_CHANGED,
          outcomeOptions: BUILDING_CHANGE_OPTIONS,
          fields: [
            {
              id: "newBuildingAddress",
              label: "New building address",
              type: "text",
              wide: true,
              showIf: [{ field: "outcome", is: "Purchased" }],
            },
            {
              id: "whichBuilding",
              label: "Which building?",
              type: "picker",
              wide: true,
              emptyLabel: "Select a building",
              noneLeftLabel: "No buildings left to select",
              showIf: [{ field: "outcome", in: ["Sold", "Renovated", "Expanded"] }],
            },
            { id: "hasMortgage", label: "Does this property have a mortgage?", type: "yesno", showIf: [BUILDING_MORTGAGE] },
            {
              id: "mortgageeName",
              label: "Mortgagee name",
              type: "text",
              wide: true,
              explain: {
                triggerLabel: "What is a mortgagee?",
                text: "A mortgagee is the bank or lender that holds the mortgage on the property.",
              },
              showIf: [{ field: "hasMortgage", is: "yes" }, BUILDING_MORTGAGE],
            },
            {
              id: "mortgageeAddress",
              label: "Mortgagee address",
              type: "text",
              wide: true,
              showIf: [{ field: "hasMortgage", is: "yes" }, BUILDING_MORTGAGE],
            },
          ],
          additionalDetailsTriggerOutcomes: ["Purchased"],
          additionalDetailsPage: {
            heading: "Commercial property - additional details",
            helpText: "We need a few more details about each property you have purchased.",
            headerField: "newBuildingAddress",
            headerFallback: "New building",
            fields: [
              {
                id: "yearBuilt",
                label: "Year built",
                type: "year",
                explain: {
                  triggerLabel: "Why do you need this?",
                  text: "If the building is over 20 years old, we will also ask when the roof, electrical, HVAC, and plumbing were last updated.",
                },
              },
              ...(
                [
                  ["roofUpdateYear", "roof"],
                  ["electricalUpdateYear", "electrical"],
                  ["hvacUpdateYear", "HVAC"],
                  ["plumbingUpdateYear", "plumbing"],
                ] as const
              ).map(
                ([id, system]): RepeatFieldSpec => ({
                  id,
                  label: `Year of last ${system} update (optional)`,
                  type: "year",
                  optional: true,
                  showIf: [{ field: "yearBuilt", olderThan: 20 }],
                }),
              ),
              { id: "buildingReplacementCost", label: "Building replacement cost", type: "currency" },
              { id: "squareFootage", label: "Square footage", type: "text" },
              { id: "sprinklered", label: "Is the building sprinklered?", type: "yesno", wide: true },
              {
                id: "centralStationFireAlarm",
                label: "Does the building have a central station fire alarm?",
                type: "yesno",
                wide: true,
              },
              {
                id: "centralStationBurglarAlarm",
                label: "Does the building have a central station burglar alarm?",
                type: "yesno",
                wide: true,
              },
            ],
          },
        },
      },
      {
        fieldId: "buildingSystemsImproved",
        number: "2",
        legend: "Have there been any significant improvements to building systems (roof, electrical, HVAC, plumbing)?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Property",
          legend: "Provide details",
          addLabel: "Add another property",
          fields: [
            {
              id: "property",
              label: "Which property?",
              type: "picker",
              wide: true,
              emptyLabel: "Select a property",
              noneLeftLabel: "No properties left to select",
              soldAware: true,
            },
            { id: "systemsImproved", label: "Which systems were improved?", type: "checkYear", wide: true, options: SYSTEM_OPTIONS },
          ],
        },
      },
      {
        fieldId: "occupancyChanged",
        number: "3",
        legend: "Have there been any changes to occupancy or building use?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
      {
        fieldId: "limitsChanged",
        number: "4",
        legend: "Any changes to the limits of insurance?",
        type: "limits",
        hintLabel: "Do I need to update buildings marked sold?",
        helpText: "No update is needed for buildings already marked sold.",
        limits: {
          legend: "Select each subject of insurance and enter the updated values",
          otherSubject: { value: "other", label: "Other" },
        },
      },
    ],
  },
  {
    sectionId: "exposure-commercial-auto",
    questions: [
      itemChangeQuestion({
        fieldId: "vehiclesChanged",
        number: "1",
        legend: "Have any vehicles been purchased, sold, or replaced?",
        itemLabel: "Vehicle",
        picker: whichItem("whichVehicle", "Which vehicle?", "Select a vehicle", "No vehicles left to select"),
        noun: "vehicle",
        kind: "vehicle",
        heading: "Commercial auto - additional details",
        helpText: "We need a few more details about each vehicle you have added.",
      }),
      personChangeQuestion({
        fieldId: "driversChanged",
        number: "2",
        legend: "Have there been any changes in drivers?",
        itemLabel: "Driver",
        noTitle: false,
        addLabel: "Add another driver",
        pickerId: "whichDriver",
        pickerLabel: "Which driver?",
        emptyLabel: "Select a driver",
      }),
      {
        fieldId: "personalVehiclesUsed",
        number: "3",
        legend: "Do any employees rent, lease, borrow, or use their personal vehicles for business purposes?",
        type: "yesno-detail",
        detailPrompt: "Provide the estimated number of employees and annual cost of hire",
      },
      {
        fieldId: "afterMarketModifications",
        number: "4",
        legend: "Do any vehicles have after-market modifications or permanently attached equipment?",
        type: "yesno-detail",
        helpText: "Examples are custom vehicle wraps, bucket lifts, hydraulic booms, hydraulic rear lift gates, etc.",
        hintLabel: "What counts as an after-market modification?",
        detailPrompt: "Provide the specific after-market modifications made and the original cost of the modifications",
      },
    ],
  },
  {
    sectionId: "exposure-commercial-watercraft",
    questions: [
      itemChangeQuestion({
        fieldId: "watercraftChanged",
        number: "1",
        legend: "Have you purchased, sold, or replaced any watercraft, engines, or trailers?",
        itemLabel: "Watercraft",
        picker: whichItem("which", "Which watercraft, engine, or trailer?", "Select an item", "No items left to select"),
        noun: "watercraft",
        kind: "watercraft",
        heading: "Commercial watercraft - additional details",
        helpText: "We need a few more details about each watercraft you have added.",
      }),
      personChangeQuestion({
        fieldId: "operatorsChanged",
        number: "2",
        legend: "Have there been any changes in operators?",
        itemLabel: "Operator",
        noTitle: true,
        addLabel: "Add another change",
        pickerId: "whichOperator",
        pickerLabel: "Which operator?",
        emptyLabel: "Select an operator",
        boating: true,
      }),
    ],
  },
  {
    sectionId: "exposure-general-liability",
    questions: [
      classCodeQuestion(),
      {
        fieldId: "newProductsOrServices",
        number: "2",
        legend: "Have any new products, services, or operations been introduced or discontinued?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
      {
        fieldId: "newContracts",
        number: "3",
        legend: "Have you entered any new contracts requiring additional insured status or special insurance provisions?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
      {
        fieldId: "subcontractsWork",
        number: "4",
        legend: "Do you subcontract any of your work?",
        type: "yesno-detail",
        detailPrompt: "Describe the work subcontracted and the estimated annual cost",
      },
    ],
  },
  {
    sectionId: "exposure-liquor-liability",
    questions: [
      {
        fieldId: "grossSalesChanged",
        number: "1",
        legend: "Are there any changes to estimated gross sales?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Sales",
          noTitle: true,
          legend: "Update your estimated gross sales",
          addLabel: "Add another change",
          fields: [
            { id: "description", label: "Select description", type: "picker", wide: true, emptyLabel: "Select a description" },
            {
              id: "estimatedGrossSales",
              label: "Estimated gross sales",
              type: "currency",
              hideUntilPicked: true,
              pickedHelp: { itemsLabel: "estimated gross sales figures" },
            },
          ],
        },
      },
    ],
  },
  { sectionId: "exposure-marina-operators-legal-liability", questions: [classCodeQuestion()] },
  { sectionId: "exposure-marine-employers-legal-liability", questions: [classCodeQuestion()] },
  {
    sectionId: "exposure-transportation",
    questions: [
      grossSalesFigureQuestion("transportation_operations_free_on_board", "annualGrossSales"),
      {
        fieldId: "unitCountsChanged",
        number: "2",
        legend:
          "Are there any changes to the number of trucks, tractors, trailers, tank-trucks or refrigerated units operated?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Units",
          single: true,
          legend: "Provide updated number of units",
          addLabel: "",
          fields: (
            [
              ["trucks", "Trucks", "trucksOperated", "truck counts"],
              ["tractors", "Tractors", undefined, undefined],
              ["trailers", "Trailers", "trailersOperated", "trailer counts"],
              ["tankTrucks", "Tank-trucks", "tankTrucksOperated", "tank-truck counts"],
              ["refrigeratedUnits", "Refrigerated units", "refrigeratedUnitsOperated", "refrigerated unit counts"],
            ] as const
          ).map(
            ([id, label, attribute, itemsLabel]): RepeatFieldSpec => ({
              id,
              label,
              type: "text",
              defaultValue: "0",
              // H2 gives a help trigger to every count it holds a figure for; it holds none for tractors.
              ...(attribute !== undefined && itemsLabel !== undefined
                ? { onFileHelp: { itemsLabel, screenKey: "transportation_operations_free_on_board", attribute } }
                : {}),
            }),
          ),
        },
      },
      {
        ...itemChangeQuestion({
          fieldId: "vehiclesChanged",
          number: "3",
          legend: "Have any vehicles been purchased, sold, or replaced?",
          itemLabel: "Vehicle",
          picker: whichItem("whichVehicle", "Which vehicle?", "Select a vehicle"),
          noun: "vehicle",
          kind: "vehicle",
          heading: "Transportation - additional details",
          helpText: "We need a few more details about each vehicle you have added.",
        }),
      },
      personChangeQuestion({
        fieldId: "driversChanged",
        number: "4",
        legend: "Have there been any changes in drivers?",
        itemLabel: "Driver",
        noTitle: true,
        addLabel: "Add another change",
        pickerId: "whichDriver",
        pickerLabel: "Which driver?",
        emptyLabel: "Select a driver",
      }),
    ],
  },
  {
    sectionId: "exposure-commercial-trucking",
    questions: [
      grossSalesFigureQuestion("truckers_principal_shipper", "grossReceiptsPastYear"),
      {
        fieldId: "commoditiesChanged",
        number: "2",
        legend:
          "Are there any changes to commodities transported, including their percentage of total revenue, and value per truck load?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Commodity",
          noTitle: true,
          legend: "Provide details",
          addLabel: "Add another change",
          fields: [
            {
              id: "commodity",
              label: "Which commodity?",
              type: "picker",
              wide: true,
              emptyLabel: "Select a commodity",
              newOption: { value: PICKER_NEW_OPTION_VALUE, label: "Add a new commodity" },
            },
            {
              id: "newCommodity",
              label: "Commodity",
              type: "text",
              wide: true,
              showIf: [{ field: "commodity", is: PICKER_NEW_OPTION_VALUE }],
            },
            { id: "percentageOfRevenue", label: "Percentage of total revenue", type: "text", pickedHelp: { itemsLabel: "revenue percentages" } },
            { id: "valuePerTruckLoad", label: "Value per truck load", type: "currency", pickedHelp: { itemsLabel: "truck load values" } },
          ],
        },
      },
      itemChangeQuestion({
        fieldId: "fleetChanged",
        number: "3",
        legend: "Have any vehicles been purchased, sold, or replaced?",
        itemLabel: "Vehicle",
        picker: whichItem("whichVehicle", "Which vehicle?", "Select a vehicle"),
        noun: "vehicle",
        kind: "vehicle",
        heading: "Commercial trucking - additional details",
        helpText: "We need a few more details about each vehicle you have added.",
      }),
      personChangeQuestion({
        fieldId: "driversChanged",
        number: "4",
        legend: "Have there been any changes in drivers?",
        itemLabel: "Driver",
        noTitle: true,
        addLabel: "Add another change",
        pickerId: "whichDriver",
        pickerLabel: "Which driver?",
        emptyLabel: "Select a driver",
      }),
    ],
  },
  {
    sectionId: "exposure-workers-compensation",
    questions: [
      {
        fieldId: "payrollChanged",
        number: "1",
        legend: "Are there any changes to estimated payroll, number of employees, or employee job duties?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Class code",
          noTitle: true,
          legend: "Update your details",
          addLabel: "Add another change",
          fields: [
            {
              id: "classCodeAndLocation",
              label: "Select class code and location",
              type: "picker",
              wide: true,
              emptyLabel: "Select a class code and location",
              noneLeftLabel: "No class codes left to select",
            },
            { id: "description", label: "Description", type: "text", wide: true, hideUntilPicked: true, pickedHelp: { itemsLabel: "class descriptions" } },
            { id: "estimatedPayroll", label: "Estimated payroll", type: "currency", hideUntilPicked: true, pickedHelp: { itemsLabel: "payroll estimates" } },
            { id: "fullTimeEmployees", label: "Full time employees", type: "text", hideUntilPicked: true, pickedHelp: { itemsLabel: "full time employee counts" } },
            { id: "partTimeEmployees", label: "Part time employees", type: "text", hideUntilPicked: true, pickedHelp: { itemsLabel: "part time employee counts" } },
          ],
        },
      },
      {
        fieldId: "remoteEmployeesChanged",
        number: "2",
        legend: "Do employees work remotely?",
        type: "yesno-detail",
        detailPrompt: "Select the state, number of employees, and applicable class code",
      },
      {
        fieldId: "employeeCountChanged",
        number: "3",
        legend: "Are there any changes to the owners, officers, or partners exemption status with the state?",
        type: "yesno-repeat",
        repeat: {
          itemLabel: "Change",
          noTitle: true,
          legend: WHAT_CHANGED,
          legendHidden: true,
          addLabel: "Add another change",
          outcomeLegend: WHAT_CHANGED,
          outcomeOptions: opts("Added", "Removed", "Changed"),
          fields: [
            { id: "name", label: "Name", type: "text", wide: true, showIf: [ADDED] },
            { id: "title", label: "Title", type: "text", wide: true, showIf: [ADDED] },
            { id: "newStatus", label: "Status", type: "radio", options: INCLUSION_OPTIONS, showIf: [ADDED] },
            {
              id: "whichPerson",
              label: "Which owner, officer, or partner?",
              type: "picker",
              wide: true,
              emptyLabel: "Select a person",
              noneLeftLabel: "No owners, officers, or partners left to select",
              showIf: [{ field: "outcome", in: ["Removed", "Changed"] }],
            },
            { id: "status", label: "Status", type: "radio", options: INCLUSION_OPTIONS, showIf: [{ field: "outcome", is: "Changed" }] },
          ],
        },
      },
    ],
  },
  {
    sectionId: "exposure-inland-marine-equipment-floater",
    questions: [
      itemChangeQuestion({
        fieldId: "equipmentChanged",
        number: "1",
        legend: "Has any equipment been purchased, sold, or replaced?",
        itemLabel: "Equipment",
        picker: whichItem("whichEquipment", "Which equipment?", "Select equipment", "No equipment left to select"),
        noun: "equipment",
        kind: "equipment",
        heading: "Inland marine / equipment floater - additional details",
        helpText: "We need a few more details about each piece of equipment you have added.",
      }),
      {
        fieldId: "equipmentLeased",
        number: "2",
        legend: "Do you rent or lease equipment from a third party?",
        type: "yesno-detail",
        detailPrompt: "Provide the estimated annual rental cost",
      },
    ],
  },
  {
    sectionId: "exposure-claims-and-loss-history",
    questions: [
      {
        fieldId: "newClaims",
        number: "1",
        legend: "Are you aware of any incidents or circumstances that could reasonably result in a potential claim?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
    ],
  },
  {
    sectionId: "exposure-additional-coverage-considerations",
    questions: [
      {
        fieldId: "additionalCoverageNeeded",
        number: "1",
        legend: "",
        type: "checkbox-list",
        checklist: {
          legend: "Please indicate if you would like information regarding any of the following coverages (optional)",
          closingNote: "This list does not represent all coverages that may be available.",
          otherItemId: "other",
          items: [
            {
              id: "commercialUmbrella",
              label: "Commercial Umbrella/Excess Liability",
              helpText:
                "Provides an extra layer of liability protection above your underlying policies, such as General Liability, Auto Liability, and Employers Liability. If a large claim exceeds the limits of those policies, an Umbrella/Excess Liability policy may help cover the remaining costs.",
            },
            {
              id: "cyberLiability",
              label: "Cyber Liability",
              helpText:
                "Protects your business against losses related to cyber incidents, such as data breaches, ransomware attacks, hacking, and other technology-related risks. It may help pay expenses such as customer notifications, data recovery, legal defense, regulatory fines, and business interruption.",
            },
            {
              id: "employmentPracticesLiability",
              label: "Employment Practices Liability",
              helpText:
                "Protects your business against claims made by employees alleging wrongful workplace practices, such as discrimination, harassment, wrongful termination, or retaliation. The policy may help cover legal defense costs, settlements, and judgments.",
            },
            {
              id: "directorsAndOfficersLiability",
              label: "Directors & Officers Liability",
              helpText:
                "Protects the personal assets of your company’s directors and officers if they are sued for decisions made while managing the business. It also may help cover legal defense costs and certain claims against the organization itself.",
            },
            {
              id: "crimeCoverage",
              label: "Crime Coverage",
              helpText:
                "Provides protection against financial losses resulting from crimes such as employee theft, forgery, fraud, funds transfer scams, counterfeit money, and certain forms of cyber-related theft. This coverage may help reimburse the business for covered stolen assets and related losses.",
            },
            {
              id: "commercialPropertyInsurance",
              label: "Commercial Property Insurance",
              helpText:
                "Protects your buildings, business personal property, and other covered physical assets from damage caused by covered events such as fire, theft, vandalism, or certain weather-related losses. It may also help cover lost income if a covered loss disrupts business operations.",
            },
            {
              id: "businessAutoInsurance",
              label: "Business Auto Insurance",
              helpText:
                "Provides coverage for vehicles owned, leased, or used by the business. It may help pay for property damage, bodily injury claims, and physical damage to covered vehicles resulting from an accident.",
            },
            {
              id: "commercialGeneralLiability",
              label: "Commercial General Liability",
              helpText:
                "Protects your business if it is held responsible for bodily injury, property damage, or personal and advertising injury to a third party. It may help cover legal defense costs, settlements, and judgments resulting from covered claims.",
            },
            {
              id: "inlandMarine",
              label: "Inland Marine",
              helpText:
                "Provides coverage for equipment, tools, inventory, or other business property that moves between locations or is temporarily stored off-site. Unlike standard property insurance, coverage often follows the property wherever it goes.",
            },
            {
              id: "professionalLiability",
              label: "Professional Liability (Errors & Omissions)",
              helpText:
                "Protects your business against claims alleging errors, omissions, negligence, or failure to deliver professional services as promised. This coverage may help pay for legal defense costs and damages related to covered professional service claims.",
            },
            {
              id: "floodInsurance",
              label: "Flood Insurance",
              helpText:
                "Provides coverage for direct physical loss or damage caused by flooding, which is typically excluded under standard property insurance policies. This may include damage from rising water, storm surge, or overflow of bodies of water.",
            },
            {
              id: "windstormHailInsurance",
              label: "Windstorm/Hail Insurance",
              helpText:
                "Provides coverage for direct physical damage caused by windstorms or hail, including damage to buildings and covered property. In some areas, windstorm coverage may be subject to a separate deductible or excluded under standard property insurance policies.",
            },
            {
              id: "earthquakeInsurance",
              label: "Earthquake Insurance",
              helpText:
                "Provides coverage for damage caused by earthquakes and other earth movement events, which are generally excluded under standard property insurance policies. This coverage may help pay to repair or replace covered property damaged by a covered earthquake event.",
            },
            { id: "other", label: "Other", helpText: "Please advise" },
          ],
        },
      },
    ],
  },
  {
    sectionId: "exposure-anything-else",
    questions: [
      {
        fieldId: "anythingElse",
        number: "1",
        legend: "Is there anything else that could affect your insurance?",
        type: "yesno-detail",
        detailPrompt: "Provide details",
      },
    ],
  },
];

const SCHEMA_BY_SECTION_ID = new Map(EXPOSURE_SCHEMAS.map((schema) => [schema.sectionId, schema]));

/** Looks up a section's question schema. Every `SectionId` whose `hostStepId` is `"exposures"`
 * has an entry here (`exposure-schema.test.ts` asserts that coverage). */
export function getExposureSectionSchema(sectionId: SectionId): ExposureSectionSchema | undefined {
  return SCHEMA_BY_SECTION_ID.get(sectionId);
}

/** The one question in `schema` whose repeat block adds additional-details pages. */
export function findAdditionalDetailsQuestion(schema: ExposureSectionSchema): ExposureQuestion | undefined {
  return schema.questions.find((question) => question.repeat?.additionalDetailsPage !== undefined);
}

/** H2 `noneLeftLabel`: "Which driver?" becomes "No drivers left to select". */
export function noneLeftLabelFor(field: RepeatFieldSpec): string {
  if (field.noneLeftLabel !== undefined) {
    return field.noneLeftLabel;
  }
  const noun = field.label
    .replace(/^Which\s+/i, "")
    .replace(/^Select\s+/i, "")
    .replace(/\?$/, "")
    .trim();
  if (noun === "") {
    return "None left to select";
  }
  let plural = `${noun}s`;
  if (/(s|x|ch|sh)$/i.test(noun)) {
    plural = `${noun}es`;
  } else if (/[^aeiou]y$/i.test(noun)) {
    plural = `${noun.slice(0, -1)}ies`;
  }
  return `No ${plural.toLowerCase()} left to select`;
}

export { EXPOSURE_SCHEMAS };
